import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  Renderer2,
  signal,
  viewChild,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { resolveBootstrapColWidths } from '../../utils/bootstrap-col-widths';

/** Concrete payload for `valueChange` (strict templates cannot bind `unknown` $event). */
export type KlSelectEmittedValue = string | number | boolean | object | null | undefined;

@Component({
  selector: 'kl-select',
  standalone: false,
  templateUrl: './kl-select.html',
  styleUrl: './kl-select.scss',
})
export class KlSelectComponent implements OnInit {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);

  readonly formGroup = input.required<FormGroup>();
  readonly controlName = input.required<string>();

  readonly label = input('');
  readonly labelDescription = input('');
  /** i18n key; defaults to "Seçilmemiş" so an unset dropdown always reads as unselected. */
  readonly placeholder = input('unselected');
  /** PrimeNG option rows; `object` covers entity rows and `{ label, value }` maps. */
  readonly options = input<object[]>([]);
  readonly optionLabel = input('label');
  readonly optionValue = input('value');
  /** When true, `optionLabel` values are ngx-translate keys (dropdown + selected label). */
  readonly translateOptionLabels = input(true);
  /** Opt-in second field shown as `"<optionLabel> - <secondaryOptionLabel>"` (not translated); also widens filter search to both fields. */
  readonly secondaryOptionLabel = input<string | null>(null);
  readonly bindWholeObject = input(false);
  /** Field used to compare the bound value against `options` (e.g. `'id'`) instead of deep-equality; needed when the bound object's shape differs from the option rows (e.g. an embedded document ref vs. a list-endpoint row). */
  readonly dataKey = input<string | undefined>(undefined);
  readonly filter = input(false);
  readonly showClear = input(false);
  readonly appendTo = input<string | HTMLElement>('body');
  /** When true, overlay panel width matches `.kl-field__control` (needed with `appendTo="body"`). */
  readonly matchPanelToTriggerWidth = input(true);
  /** Forwarded to PrimeNG `p-select` loading state (spinner on the control). */
  readonly loading = input(false);
  readonly fluid = input(true);

  readonly col = input('12');
  readonly sm = input<string | undefined>(undefined);
  readonly md = input<string | undefined>(undefined);
  readonly lg = input<string | undefined>(undefined);
  readonly xl = input<string | undefined>(undefined);
  readonly marginY = input(false);
  readonly marginBottom = input(true);

  /** When true, show validation error text under the control. */
  readonly showFieldError = input(false);

  readonly inputId = input('');

  readonly valueChange = output<KlSelectEmittedValue>();

  readonly fieldControl = viewChild.required<ElementRef<HTMLElement>>('fieldControl');

  readonly panelOverlayStyle = signal<Record<string, string> | null>(null);

  readonly control = computed<AbstractControl | null>(() => this.formGroup()?.get(this.controlName()) ?? null);

  /**
   * `control.invalid`/`.touched`/`.errors` are plain mutable state, not signals, so a `computed()`
   * reading them never re-runs on its own — e.g. `form.markAllAsTouched()` from a save handler
   * wouldn't turn on this field's red border. Bump this on every control event so the
   * validation-derived computeds below stay live.
   */
  private readonly controlState = signal(0);
  private readonly controlStateSync = effect((onCleanup) => {
    const sub = this.control()?.events.subscribe(() => this.controlState.update((v) => v + 1));
    onCleanup(() => sub?.unsubscribe());
  });

  readonly fieldId = computed(() => (this.inputId() || this.controlName() || 'kl-select').replace(/\s/g, '_'));

  readonly required = computed(() => {
    this.controlState();
    const c = this.control();
    if (!c?.validator) return false;
    const v = c.validator({} as AbstractControl);
    return !!v?.['required'];
  });

  readonly showInvalid = computed(() => {
    this.controlState();
    const c = this.control();
    return !!(c && c.invalid && c.touched);
  });

  readonly controlError = computed(() => {
    this.controlState();
    const c = this.control();
    if (!c?.invalid || !c.touched) return null;
    if (c.errors?.['required']) return 'This field is required.';
    return null;
  });

  ngOnInit(): void {
    const el = this.host.nativeElement;
    const col = this.col();
    const { sm, md, lg, xl } = resolveBootstrapColWidths(col, this.sm(), this.md(), this.lg(), this.xl());
    this.renderer.addClass(el, 'col-' + col);
    this.renderer.addClass(el, 'col-sm-' + sm);
    this.renderer.addClass(el, 'col-md-' + md);
    this.renderer.addClass(el, 'col-lg-' + lg);
    this.renderer.addClass(el, 'col-xl-' + xl);
  }

  onAnyChange(): void {
    const v = this.control()?.value;
    if (v === undefined) {
      this.valueChange.emit(undefined);
      return;
    }
    if (v === null) {
      this.valueChange.emit(null);
      return;
    }
    const t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      this.valueChange.emit(v);
      return;
    }
    if (t === 'object') {
      this.valueChange.emit(v);
      return;
    }
    this.valueChange.emit(undefined);
  }

  onSelectOverlayShow(): void {
    if (!this.matchPanelToTriggerWidth()) return;
    const el = this.fieldControl().nativeElement;
    const w = Math.round(el.getBoundingClientRect().width);
    if (w <= 0) return;
    this.panelOverlayStyle.set({
      width: `${w}px`,
      maxWidth: 'min(100vw - 1rem, 100%)',
    });
  }

  onSelectOverlayHide(): void {
    this.panelOverlayStyle.set(null);
  }
}
